import { FormattedMessage } from 'react-intl';
import { type ReactNode } from 'react';
import cx from 'clsx';
import { useMoneyFormatter } from '@repo/design-system-kit';
import { BasketHeader, BasketItem, BasketSummary } from './internal/components';
import { type BasketProps } from './internal/types';
import { calculateBasketTotals } from './internal/utils/calculate-basket-totals';
import styles from './basket.module.css';

export function Basket({
  items,
  amount,
  payee,
  className,
  isPreview = false,
}: BasketProps): ReactNode {
  const { formatMoney } = useMoneyFormatter();
  const totals = calculateBasketTotals(items);
  const { currency } = amount;

  const formattedTotal = formatMoney({
    amount: parseFloat(amount.value),
    currency,
  });

  return (
    <div className={cx(styles.container, className, isPreview && styles.preview)}>
      <h2 className={cx(styles.title, 'title-2')}>{formattedTotal}</h2>
      <h3 className={cx(styles.subtitle, 'title-4')}>
        <FormattedMessage
          id="payment-link.payment-details.title"
          values={{ merchantCompany: payee }}
        />
      </h3>
      {items.length > 0 ? (
        <table className={styles.table}>
          <colgroup>
            <col className={cx(styles['col-item'], isPreview && styles['width-mixin'])} />
            <col className={cx(styles['col-quantity'], styles['width-mixin'])} />
            <col className={cx(styles['col-vat'], styles['width-mixin'])} />
            <col className={cx(styles['col-total'], styles['width-mixin'])} />
          </colgroup>

          <BasketHeader isPreview={isPreview} />

          <tbody className={styles.itemisation}>
            {items.map((item, index) => (
              <BasketItem
                currency={currency}
                isPreview={isPreview}
                item={item}
                itemTotal={totals.itemTotals[index] ?? 0}
                key={item.id || `${item.title}-${index}`}
              />
            ))}
          </tbody>

          <BasketSummary currency={currency} isPreview={isPreview} totals={totals} />
        </table>
      ) : null}
    </div>
  );
}
